// Shared bottom sheet primitive: portal, backdrop, escape-key close,
// scroll-lock, drag handle, sticky header/footer. Modeled on
// TeamFilterSheet.js and ReplaceTeamModal.js, the two most complete existing
// sheets in the app. New sheets (league switcher, bid, confirm actions)
// should be built on this rather than reimplementing the shell.
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { ModalPortal, useLockBodyScroll } from './ModalPortal';
import { useSheetTransition, prefersReducedMotion, SHEET_ENTER_MS, SHEET_EXIT_MS } from './useSheetTransition';
import { useSheetDrag } from './useSheetDrag';

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  footer,
  maxHeight = '85vh',
  // Which edge the sheet is attached to. Bottom is the default and what every
  // existing caller gets: a sheet that acts on the content under your thumb
  // belongs by the thumb. A sheet opened from a control in the nav bar does
  // not, because it would fly past the thing that opened it and land at the
  // far end of the screen. The league switcher hangs from the top for that
  // reason, under the caret that opens it.
  anchor = 'bottom',
}) {
  const fromTop = anchor === 'top';
  // `mounted` outlives `open` by the length of the exit, so the sheet can
  // slide down before it leaves the tree. Everything else keys off `open`:
  // the scroll lock releases and Escape stops firing the moment the caller
  // closes, not when the animation finishes.
  const { mounted, entered } = useSheetTransition(open);
  const { dragY, dragging, dragHandlers } = useSheetDrag(onClose, fromTop ? 'up' : 'down');
  const reduceMotion = prefersReducedMotion();
  // Entering is slower than leaving: see the constants. While a finger is on
  // the sheet there is no transition at all, or the sheet trails the drag.
  const duration = reduceMotion ? 0 : (entered ? SHEET_ENTER_MS : SHEET_EXIT_MS);

  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <ModalPortal open={mounted}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          // The sheet is still mounted through its exit, so without this the
          // backdrop keeps swallowing taps for a quarter of a second after
          // the user has dismissed it.
          pointerEvents: entered ? 'auto' : 'none',
        }}
      >
        <div
          onClick={onClose}
          style={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            // Fades out with the drag, so a sheet pulled halfway down is
            // already showing more of the page behind it. Floored at a fifth
            // of full strength: the backdrop is also what is catching taps,
            // and one that looks gone but still blocks is worse than one that
            // stays faintly visible.
            opacity: entered ? Math.max(0.2, 1 - dragY / 400) : 0,
            transition: dragging ? 'none' : `opacity ${duration}ms ease`,
          }}
        />

        <div
          style={{
            position: 'absolute', left: 0, right: 0,
            ...(fromTop ? { top: 0 } : { bottom: 0 }),
            backgroundColor: 'var(--color-bg-card)',
            ...(fromTop
              ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }
              : { borderTopLeftRadius: 20, borderTopRightRadius: 20 }),
            maxHeight, display: 'flex', flexDirection: 'column',
            ...(fromTop
              ? { paddingTop: 'env(safe-area-inset-top)' }
              : { paddingBottom: 'env(safe-area-inset-bottom)' }),
            boxShadow: fromTop ? '0 8px 32px rgba(0,0,0,0.18)' : '0 -8px 32px rgba(0,0,0,0.18)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            // translateY animates on the compositor, so the slide holds 60fps
            // on a mid-range phone in a way animating `bottom` would not.
            // The curve is decelerated rather than eased at both ends: a sheet
            // that slows into place reads as physical, one that eases out of
            // rest as well reads as sluggish.
            transform: entered
              ? `translateY(${fromTop ? -dragY : dragY}px)`
              : `translateY(${fromTop ? '-100%' : '100%'})`,
            // No transition while a finger is down, or the sheet lags behind
            // the drag by the length of the curve.
            transition: dragging ? 'none' : `transform ${duration}ms cubic-bezier(0.32, 0.72, 0, 1)`,
            willChange: 'transform',
          }}
        >
          {/* The handle and the title row are the drag area. The scrolling
              body below is not: a sheet that dismissed on any downward swipe
              could never be scrolled back up. */}
          {!fromTop && (
            <div
              {...dragHandlers}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, touchAction: 'none', cursor: 'grab' }}
            >
              <div style={{ width: 44, height: 5, borderRadius: 999, backgroundColor: 'var(--color-border)' }} />
            </div>
          )}

          {title && (
            <div
              {...dragHandlers}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: fromTop ? '16px 20px 4px' : '12px 20px 4px', touchAction: 'none',
              }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)', margin: 0 }}>
                {title}
              </h2>
              <button
                onClick={onClose}
                aria-label="Close"
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  padding: 8, minWidth: 44, minHeight: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--color-text-muted)', lineHeight: 0, WebkitTapHighlightColor: 'transparent',
                }}
              >
                <X size={22} />
              </button>
            </div>
          )}

          <div style={{ overflowY: 'auto', padding: '8px 20px 20px', flex: 1 }}>
            {children}
          </div>

          {fromTop && (
            <div
              {...dragHandlers}
              style={{ display: 'flex', justifyContent: 'center', paddingBottom: 10, touchAction: 'none', cursor: 'grab' }}
            >
              <div style={{ width: 44, height: 5, borderRadius: 999, backgroundColor: 'var(--color-border)' }} />
            </div>
          )}

          {footer && (
            <div style={{
              padding: '12px 20px 20px',
              borderTop: '1px solid var(--color-bg-subtle)',
              backgroundColor: 'var(--color-bg-card)',
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}
